/**
 * ManagerDetailModal
 * Read-only side-sheet showing all relevant information for a given manager:
 *  - User profile data
 *  - Associated company
 *  - Assigned branch
 */

import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  User as UserIcon,
  Building2,
  Store,
  Mail,
  AtSign,
  CalendarDays,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Boxes,
  DollarSign,
  Package,
  BarChart3,
  Users,
  Tag,
} from "lucide-react";
import { usersService, type User } from "@/services/usersService";
import { getCompanyById, type Company } from "@/services/companiesService";
import { getBranchById, type Branch } from "@/services/branchesService";
import UserAvatar from "@/components/UserAvatar";

interface ManagerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  manager: User | null;
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-white/40">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-white/40 mb-0.5">{label}</p>
        <p className="text-sm text-white break-words">
          {value || <span className="text-white/30 italic">Sin dato</span>}
        </p>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">
      {children}
    </p>
  );
}

export default function ManagerDetailModal({
  isOpen,
  onClose,
  manager,
}: ManagerDetailModalProps) {
  const [company, setCompany] = useState<Company | null>(null);
  const [branch, setBranch] = useState<Branch | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !manager) {
      setCompany(null);
      setBranch(null);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const [companyData, branchData] = await Promise.all([
          manager.company_id ? getCompanyById(manager.company_id) : Promise.resolve(null),
          manager.branch_id ? getBranchById(manager.branch_id) : Promise.resolve(null),
        ]);
        setCompany(companyData);
        setBranch(branchData);
      } catch (err) {
        console.error("Error loading manager details:", err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [isOpen, manager]);

  if (!manager) return null;

  const fullName = usersService.getFullName(manager);
  const initials = usersService.getInitials(manager);
  const createdAt = manager.created_at
    ? new Date(manager.created_at).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-white/10 text-white p-0 flex flex-col h-[100dvh] overflow-hidden"
      >
        {/* Header ─────────────────────────────────────── */}
        <SheetHeader
          className="flex-shrink-0 px-6 pb-4 border-b border-white/10"
          style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-4">
            <UserAvatar initials={initials} role="manager" size="xl" />
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg font-semibold leading-tight">
                {fullName}
              </SheetTitle>
              <p className="text-sm text-white/50 mt-0.5">@{manager.username}</p>
              <div className="mt-2">
                <Badge
                  className={
                    manager.is_active
                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                      : "bg-red-500/20 text-red-400 border-red-500/30"
                  }
                >
                  {manager.is_active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Scrollable body ─────────────────────────────── */}
        <ScrollArea className="flex-1 min-h-0 px-6 py-5">
          <div className="space-y-6">

            {/* Usuario ─────────────────────────────────── */}
            <section className="space-y-3">
              <SectionTitle>
                <UserIcon className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Información del usuario
              </SectionTitle>
              <InfoRow icon={<AtSign className="h-4 w-4" />} label="Usuario" value={manager.username} />
              <InfoRow icon={<Mail className="h-4 w-4" />} label="Correo electrónico" value={manager.email} />
              <InfoRow icon={<UserIcon className="h-4 w-4" />} label="Nombre completo" value={fullName} />
              <InfoRow icon={<CalendarDays className="h-4 w-4" />} label="Registrado el" value={createdAt} />
            </section>

            <Separator className="bg-white/10" />

            {/* Empresa ─────────────────────────────────── */}
            <section className="space-y-3">
              <SectionTitle>
                <Building2 className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Empresa
              </SectionTitle>

              {isLoading ? (
                <Skeleton className="h-16 w-full rounded-xl bg-white/10" />
              ) : company ? (
                <div className="bg-white/5 rounded-xl px-4 py-3 border border-white/10 space-y-2">
                  <div className="flex items-center gap-3">
                    <UserAvatar role="company" size="sm" icon={<Building2 className="h-4 w-4" />} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{company.name}</p>
                      <Badge
                        className={
                          company.is_active
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mt-1"
                            : "bg-red-500/20 text-red-400 border-red-500/30 mt-1"
                        }
                      >
                        {company.is_active ? "Activa" : "Inactiva"}
                      </Badge>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-white/30 italic">Sin empresa asignada</p>
              )}
            </section>

            <Separator className="bg-white/10" />

            {/* Sucursal asignada ────────────────────────── */}
            <section className="space-y-3">
              <SectionTitle>
                <Store className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Sucursal asignada
              </SectionTitle>

              {isLoading ? (
                <Skeleton className="h-14 w-full rounded-xl bg-white/10" />
              ) : branch ? (
                <div className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                  <div className="flex items-center gap-3 min-w-0">
                      <UserAvatar role="branch" size="sm" icon={<Store className="h-4 w-4" />} />
                    <p className="text-sm text-white font-medium truncate">{branch.name}</p>
                  </div>
                  <Badge
                    className={
                      branch.is_active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 ml-2 flex-shrink-0"
                        : "bg-red-500/20 text-red-400 border-red-500/30 ml-2 flex-shrink-0"
                    }
                  >
                    {branch.is_active ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
              ) : (
                <p className="text-sm text-white/30 italic">Sin sucursal asignada</p>
              )}
            </section>

            <Separator className="bg-white/10" />

            {/* Permisos del panel ─────────────────────── */}
            <section className="space-y-3">
              <SectionTitle>
                <ShieldCheck className="inline-block h-3 w-3 mr-1 mb-0.5" />
                Acceso al panel de administración
              </SectionTitle>

              {(() => {
                const mc = (manager.modules_config ?? {}) as Record<string, any>;
                const stock = mc.stock;
                const stockEnabled =
                  stock === true ||
                  !!(
                    typeof stock === "object" &&
                    stock &&
                    (stock.view || stock.create || stock.edit || stock.delete || stock.adjust)
                  );
                const stockSubs =
                  typeof stock === "object" && stock
                    ? [
                        { label: "Ver listado", enabled: !!(stock.view || stock.adjust) },
                        { label: "Agregar", enabled: !!(stock.create || stock.adjust) },
                        { label: "Editar", enabled: !!(stock.edit || stock.adjust) },
                        { label: "Reducir", enabled: !!(stock.delete || stock.adjust) },
                      ]
                    : stock === true
                      ? [
                          { label: "Ver listado", enabled: true },
                          { label: "Agregar", enabled: true },
                          { label: "Editar", enabled: true },
                          { label: "Reducir", enabled: true },
                        ]
                      : undefined;

                const items: {
                  icon: React.ReactNode;
                  label: string;
                  enabled: boolean;
                  subs?: { label: string; enabled: boolean }[];
                }[] = [
                  {
                    icon: <Boxes className="h-4 w-4" />,
                    label: "Inventario / Stock",
                    enabled: stockEnabled,
                    subs: stockSubs,
                  },
                  {
                    icon: <DollarSign className="h-4 w-4" />,
                    label: "Precios",
                    enabled: !!mc.prices,
                  },
                  {
                    icon: <Package className="h-4 w-4" />,
                    label: "Productos",
                    enabled: !!(
                      mc.products?.view ||
                      mc.products?.create ||
                      mc.products?.edit ||
                      mc.products?.delete
                    ),
                    subs: [
                      { label: "Ver listado", enabled: !!mc.products?.view },
                      { label: "Crear", enabled: !!mc.products?.create },
                      { label: "Editar", enabled: !!mc.products?.edit },
                    ],
                  },
                  {
                    icon: <Users className="h-4 w-4" />,
                    label: "Clientes",
                    enabled: !!mc.customers,
                  },
                  {
                    icon: <Tag className="h-4 w-4" />,
                    label: "Descuentos",
                    enabled: !!mc.discounts,
                  },
                  {
                    icon: <BarChart3 className="h-4 w-4" />,
                    label: "Resumen financiero",
                    enabled: !!(
                      mc["financial-summary"]?.view ||
                      mc["financial-summary"]?.ventas ||
                      mc["financial-summary"]?.turnos
                    ),
                    subs: [
                      { label: "Ver resumen general", enabled: !!mc["financial-summary"]?.view },
                      { label: "Ventas", enabled: !!mc["financial-summary"]?.ventas },
                      { label: "Turnos", enabled: !!mc["financial-summary"]?.turnos },
                    ],
                  },
                ];

                return (
                  <div className="space-y-2">
                    {items.map((item) => (
                      <div
                        key={item.label}
                        className={`rounded-xl border px-4 py-3 space-y-2 ${
                          item.enabled
                            ? "bg-white/5 border-white/10"
                            : "bg-transparent border-white/5 opacity-50"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span className="text-white/40">{item.icon}</span>
                            <p className="text-sm font-medium text-white">{item.label}</p>
                          </div>
                          {item.enabled ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-4 w-4 text-white/20 flex-shrink-0" />
                          )}
                        </div>
                        {item.subs && item.enabled && (
                          <div className="ml-7 border-l border-white/10 pl-3 space-y-1">
                            {item.subs.map((sub) => (
                              <div key={sub.label} className="flex items-center justify-between gap-2">
                                <p className="text-xs text-white/50">{sub.label}</p>
                                {sub.enabled ? (
                                  <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                                ) : (
                                  <XCircle className="h-3 w-3 text-white/20" />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </section>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
